import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Users, ShoppingBag, Globe, Sparkle } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { useAuth } from "../lib/AuthContext";
import { api, API } from "../lib/api";
import { toast } from "sonner";

export default function Admin() {
    const { user, loading } = useAuth();
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [busy, setBusy] = useState(true);
    const [denied, setDenied] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user || user === false) return;
        (async () => {
            try {
                const [s, o, w] = await Promise.all([
                    api.get("/admin/stats"),
                    api.get("/admin/orders", { params: { limit: 200 } }),
                    api.get("/admin/waitlist", { params: { limit: 200 } }),
                ]);
                setStats(s.data);
                setOrders(o.data.orders || []);
                setWaitlist(w.data.waitlist || []);
            } catch (err) {
                if (err.response?.status === 403) setDenied(true);
                else toast.error("Could not load admin data.");
            } finally { setBusy(false); }
        })();
    }, [user, loading]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="admin-loading"><ForgetMeNotIcon size={48} className="animate-pulse" /></div>;
    }
    if (!user || user === false) return <Navigate to="/sign-in" replace />;
    if (denied) {
        return (
            <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-denied">
                <SiteNav />
                <main className="pt-32 pb-24 px-6 text-center">
                    <ForgetMeNotIcon size={48} className="mx-auto mb-6" />
                    <h1 className="font-serif text-3xl text-[#2D2B2A]">This area is for Glenn & Lea only.</h1>
                    <p className="mt-3 text-[#5C5A59]">If that's you, sign in with the admin email.</p>
                    <Link to="/dashboard" className="mt-6 inline-block text-[#6B7DB8] underline">Back to dashboard</Link>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-page">
            <SiteNav />
            <main className="pt-28 pb-24 px-6 rem-watercolor-bg min-h-screen">
                <div className="max-w-6xl mx-auto">
                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-3">For Glenn & Lea</p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight" data-testid="admin-heading">
                            The <em className="italic text-[#4A5A95]">demand signal</em>
                        </h1>
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4D068]/30 border border-[#F4D068]/60 text-xs text-[#8A6A0E]">
                            <Sparkle size={12} weight="fill" /> Launch mode: {stats?.launch_mode || "pre"} · No money is being taken
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Link to="/admin/model" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all" data-testid="admin-model-link">
                                💹 Open Revenue Model
                            </Link>
                            <Link to="/admin/font-shootout" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D98E5F] hover:bg-[#C97B4A] text-white text-sm font-medium transition-all" data-testid="admin-font-shootout-link">
                                🔤 Font Shootout
                            </Link>
                            <a href={`${API}/admin/export/font-shootout.pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6A982] hover:bg-[#D98E5F] text-white text-sm font-medium transition-all" data-testid="admin-font-shootout-pdf-link">
                                🔤 Font Shootout PDF
                            </a>
                            <a href={`${API}/admin/export/business-plan.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F4D068] hover:bg-[#E6B844] text-[#2D2B2A] text-sm font-medium transition-all" data-testid="admin-bp-link">
                                📄 Business Plan PDF
                            </a>
                            <a href={`${API}/admin/export/im-pack.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5A95] hover:bg-[#3A4878] text-white text-sm font-medium transition-all" data-testid="admin-im-link">
                                📘 IM Pack PDF
                            </a>
                            <a href={`${API}/admin/export/tablet-brief.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2D2B2A] hover:bg-[#4a4644] text-white text-sm font-medium transition-all" data-testid="admin-tablet-brief-link">
                                🔧 Tablet Brief PDF
                            </a>
                            <a href={`${API}/admin/export/fulfilment-process.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all" data-testid="admin-fulfilment-link">
                                📦 Fulfilment Process PDF
                            </a>
                            <a href={`${API}/admin/export/ownership-confirmation.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F4D068] hover:bg-[#E6B844] text-[#2D2B2A] text-sm font-medium transition-all" data-testid="admin-ownership-link">
                                🏛️ Ownership Confirmation PDF
                            </a>
                            <a href={`${API}/admin/export/demo-runsheet.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5A95] hover:bg-[#3A4878] text-white text-sm font-medium transition-all" data-testid="admin-runsheet-link">
                                🎬 Demo Run Sheet PDF
                            </a>
                            <a href={`${API}/admin/export/video-script.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2D2B2A] hover:bg-[#4a4644] text-white text-sm font-medium transition-all" data-testid="admin-video-script-link">
                                🎥 Brand Film Script PDF
                            </a>
                            <a href={`${API}/admin/export/tutorial.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all" data-testid="admin-tutorial-link">
                                📖 Family Tutorial PDF
                            </a>
                            <a href={`${API}/admin/export/pet-edition.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D98E5F] hover:bg-[#C97B4A] text-white text-sm font-medium transition-all" data-testid="admin-pet-edition-link">
                                🐾 Pet Edition Strategy PDF
                            </a>
                            <a href={`${API}/admin/export/strategy-appendix.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5A95] hover:bg-[#2F3D6E] text-white text-sm font-medium transition-all" data-testid="admin-strategy-appendix-link">
                                📊 Strategy Appendix (IM/Pitch/Birchal)
                            </a>
                            <a href={`${API}/admin/export/markets-evidence.pdf`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all" data-testid="admin-markets-evidence-link">
                                🌍 Global Markets Evidence (13 locales)
                            </a>
                            <a href={`${API}/admin/export/partner-eois.csv`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5E1D8] hover:border-[#8C9ECB] text-[#4A5A95] text-sm font-medium transition-all" data-testid="admin-eoi-csv-link">
                                🤝 Partner EOIs CSV
                            </a>
                        </div>
                    </motion.div>

                    {busy ? (
                        <div className="mt-12 text-center"><ForgetMeNotIcon size={40} className="animate-pulse mx-auto" /></div>
                    ) : !stats ? null : (
                        <>
                            {/* Top tiles */}
                            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <Tile label="Waitlist" value={stats.waitlist.total} sub={`+${stats.waitlist.last_7_days} last 7d`} icon={Users} testid="tile-waitlist" />
                                <Tile label="Reservations" value={stats.orders.total} sub={`+${stats.orders.last_7_days} last 7d`} icon={ShoppingBag} testid="tile-orders" />
                                <Tile label="Indicated revenue" value={`$${(stats.orders.estimated_total_revenue_usd || 0).toLocaleString()}`} sub="if all converted" highlighted testid="tile-revenue" />
                                <Tile label="Countries" value={(stats.geography || []).length} sub="reaching" icon={Globe} testid="tile-countries" />
                            </div>

                            {/* Plan split */}
                            <section className="mt-10 bg-white rounded-3xl p-8 border border-[#E5E1D8]/70 shadow-rem-soft" data-testid="plan-split">
                                <h2 className="font-serif text-2xl text-[#2D2B2A]">Plan preference (for China MOQ planning)</h2>
                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <PlanBar label="Software ($100)" count={stats.orders.plan_split.software || 0} total={stats.orders.total || 1} color="#8C9ECB" />
                                    <PlanBar label="Bundle ($150)" count={stats.orders.plan_split.bundle || 0} total={stats.orders.total || 1} color="#4A5A95" />
                                </div>
                                <p className="mt-6 text-sm text-[#8A8786]">
                                    Bundle units = how many tablets+headphones you'll need from the factory. Multiply by 1.3x for buffer.
                                </p>
                            </section>

                            {/* Waitlist source breakdown */}
                            {Array.isArray(stats.waitlist.sources) && stats.waitlist.sources.length > 0 && (
                                <section className="mt-8 bg-white rounded-3xl p-8 border border-[#E5E1D8]/70 shadow-rem-soft" data-testid="waitlist-sources">
                                    <h2 className="font-serif text-2xl text-[#2D2B2A]">Where signups come from</h2>
                                    <p className="text-sm text-[#8A8786] mt-1">High-intent emails captured at the end of John's demo carry the <em className="italic">john_demo_end</em> tag.</p>
                                    <div className="mt-5 space-y-2">
                                        {stats.waitlist.sources.map((s) => (
                                            <div key={s.source} className="flex items-center gap-3" data-testid={`source-${s.source}`}>
                                                <span className="text-xs px-2.5 py-1 rounded-full bg-[#F4F6FB] text-[#4A5A95] font-semibold tracking-wide uppercase">{s.source}</span>
                                                <div className="flex-1 h-2 rounded-full bg-[#F4F6FB] overflow-hidden">
                                                    <div className="h-full bg-gradient-to-r from-[#8C9ECB] to-[#4A5A95]" style={{ width: `${Math.min(100, Math.round((s.count / Math.max(1, stats.waitlist.total)) * 100))}%` }} />
                                                </div>
                                                <span className="text-sm text-[#2D2B2A] font-medium w-12 text-right">{s.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Geography */}
                            <section className="mt-8 bg-white rounded-3xl p-8 border border-[#E5E1D8]/70 shadow-rem-soft" data-testid="geography">
                                <h2 className="font-serif text-2xl text-[#2D2B2A]">Where families are</h2>
                                {stats.geography.length === 0 ? (
                                    <p className="mt-4 text-sm text-[#8A8786]">No country data yet — we'll start asking on the form when reservations come in.</p>
                                ) : (
                                    <div className="mt-5 space-y-2">
                                        {stats.geography.slice(0, 12).map((c) => (
                                            <div key={c.country} className="flex justify-between items-center text-sm" data-testid={`country-${c.country}`}>
                                                <span className="text-[#5C5A59]">{c.country}</span>
                                                <span className="text-[#4A5A95] font-medium">{c.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* Recent reservations */}
                            <section className="mt-8 bg-white rounded-3xl p-8 border border-[#E5E1D8]/70 shadow-rem-soft" data-testid="recent-orders">
                                <div className="flex items-center justify-between mb-5">
                                    <h2 className="font-serif text-2xl text-[#2D2B2A]">Recent reservations</h2>
                                    <a href={`${API}/admin/export/orders.csv`} className="inline-flex items-center gap-2 text-sm text-[#6B7DB8] hover:text-[#4A5A95]" data-testid="export-orders-csv">
                                        <Download size={14} /> Export CSV
                                    </a>
                                </div>
                                {orders.length === 0 ? (
                                    <p className="text-sm text-[#8A8786]">No reservations yet — the China-trip ammunition is being built. Drive a few friends to your pricing page.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-xs uppercase tracking-[0.2em] text-[#8A8786] border-b border-[#E5E1D8]/70">
                                                    <th className="py-2 pr-4">When</th>
                                                    <th className="py-2 pr-4">Email</th>
                                                    <th className="py-2 pr-4">Plan</th>
                                                    <th className="py-2 pr-4">Name</th>
                                                    <th className="py-2 pr-4">Country</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orders.slice(0, 25).map((o) => (
                                                    <tr key={o.id} className="border-b border-[#E5E1D8]/40">
                                                        <td className="py-2 pr-4 text-[#8A8786] text-xs">{(o.created_at || "").slice(0, 10)}</td>
                                                        <td className="py-2 pr-4 text-[#2D2B2A]">{o.email}</td>
                                                        <td className="py-2 pr-4">
                                                            <span className={`px-2 py-0.5 rounded-full text-xs ${o.plan === "bundle" ? "bg-[#4A5A95] text-white" : "bg-[#F4F6FB] text-[#4A5A95]"}`}>
                                                                {o.plan}{o.is_upgrade && " ↑"}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 pr-4 text-[#5C5A59]">{o.name || "—"}</td>
                                                        <td className="py-2 pr-4 text-[#5C5A59]">{o.country || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            {/* Waitlist */}
                            <section className="mt-8 bg-white rounded-3xl p-8 border border-[#E5E1D8]/70 shadow-rem-soft" data-testid="recent-waitlist">
                                <div className="flex items-center justify-between mb-5">
                                    <h2 className="font-serif text-2xl text-[#2D2B2A]">Waitlist — soft signups</h2>
                                    <a href={`${API}/admin/export/waitlist.csv`} className="inline-flex items-center gap-2 text-sm text-[#6B7DB8] hover:text-[#4A5A95]" data-testid="export-waitlist-csv">
                                        <Download size={14} /> Export CSV
                                    </a>
                                </div>
                                {waitlist.length === 0 ? (
                                    <p className="text-sm text-[#8A8786]">No waitlist signups yet.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-xs uppercase tracking-[0.2em] text-[#8A8786] border-b border-[#E5E1D8]/70">
                                                    <th className="py-2 pr-4">When</th>
                                                    <th className="py-2 pr-4">Email</th>
                                                    <th className="py-2 pr-4">Name</th>
                                                    <th className="py-2 pr-4">Building for</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {waitlist.slice(0, 25).map((w) => (
                                                    <tr key={w.id} className="border-b border-[#E5E1D8]/40">
                                                        <td className="py-2 pr-4 text-[#8A8786] text-xs">{(w.created_at || "").slice(0, 10)}</td>
                                                        <td className="py-2 pr-4 text-[#2D2B2A]">{w.email}</td>
                                                        <td className="py-2 pr-4 text-[#5C5A59]">{w.name || "—"}</td>
                                                        <td className="py-2 pr-4 text-[#5C5A59]">{w.relation || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            {/* Platform */}
                            <section className="mt-8 grid grid-cols-3 gap-4">
                                <Tile label="Accounts" value={stats.platform.users} testid="tile-users" />
                                <Tile label="Profiles built" value={stats.platform.profiles} testid="tile-profiles" />
                                <Tile label="Memories" value={stats.platform.memories} testid="tile-memories" />
                            </section>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

function Tile({ label, value, sub, icon: Icon, highlighted, testid }) {
    return (
        <div className={`rounded-2xl p-5 ${highlighted ? "bg-gradient-to-br from-[#6B7DB8] to-[#4A5A95] text-white" : "bg-white border border-[#E5E1D8]/70"}`} data-testid={testid}>
            {Icon && <Icon size={18} weight="light" className={highlighted ? "text-white/85 mb-2" : "text-[#8C9ECB] mb-2"} />}
            <p className={`text-xs uppercase tracking-[0.2em] mb-1 ${highlighted ? "text-white/85" : "text-[#6B7DB8]"}`}>{label}</p>
            <p className={`font-serif text-3xl ${highlighted ? "text-white" : "text-[#2D2B2A]"}`}>{value}</p>
            {sub && <p className={`text-xs mt-1 ${highlighted ? "text-white/70" : "text-[#8A8786]"}`}>{sub}</p>}
        </div>
    );
}

function PlanBar({ label, count, total, color }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div>
            <div className="flex justify-between text-sm mb-2">
                <span className="text-[#2D2B2A] font-medium">{label}</span>
                <span className="text-[#5C5A59]">{count} · {pct}%</span>
            </div>
            <div className="h-3 rounded-full bg-[#F4F6FB] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}
